import { useContext, useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Container, Button } from "react-bootstrap";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { ExaminationHistory, ExamineeStatus, ExamineeStatusReason } from '../Dashboard/Exams/Models/ExaminationHistory';
import { getDateWithOffset } from "../Shared/Utility/GetDateWithOffset";
import { Link } from "react-router-dom";
import { ExaminationGrade } from "./Enums/ExaminationGrade";
import { AbccAssessment, AbccAssessmentModule, AbccAssessmentStatus } from "../Dashboard/ContinuingCertification/Models/CCAssessment";


export default function ExamHistory() {
    const validInitialSpecialtyCertYear = 2017;
    const validForAnyExamAdministeredYear = 2021;
    const [examHistory, setExamHistory] = useState<Array<ExaminationHistory>>(null);
    const [abccAssessmentHistory, setAbccAssessmentHistory] = useState<Array<AbccAssessment>>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDownloadingExamResult, setIsDownloadingExamResult] = useState(false);
    const [isDownloadingAbccAssessmentResult, setIsDownloadingAbccAssessmentResult] = useState(false);
    const [examinationIdDownloading, setExaminationIdDownloading] = useState<number>(null);
    const [abccAssessmentIdDownloading, setAbccAssessmentIdDownloading] = useState<number>(null);

    useEffect(() => {
        const getExams = () => {
            abpnClient.get<Array<ExaminationHistory>>(`/api/Examination/GetExaminationHistory`, authContext)
                .then(exams => {
                    setExamHistory(exams);
                    setIsLoading(false);
                });
        }

        const getAbccAssessments = async () => {
                    const abccAssessmentsResult = await abpnClient.get<Array<AbccAssessment>>(
                        `/api/physician/AbccAssessments`, 
                        authContext
                    );
                
                    const assessments = abccAssessmentsResult.map(
                        assessment => new AbccAssessment(assessment)
                    );
                
                    setAbccAssessmentHistory(assessments);
                };

        getExams();
        getAbccAssessments();
    }, []);

    const authContext = useContext(AuthContext);

    const getGrade = (exam: ExaminationHistory) => {
        if (exam.examineeStatusReasonId === ExamineeStatusReason.Invalidated) return exam.examineeStatusReason;

        if (exam.examinationGrade === ExaminationGrade.Pass) return "Pass";

        if (exam.examinationGrade === ExaminationGrade.Fail) return "Fail";

        if (exam.examineeStatus === ExamineeStatus.UnexcusedAbsence) return "Unexcused Absence";

        if (exam.examinationGrade === null) return "N/A"

        return "";
    }

    const getCandidateStatusDescription = (exam: ExaminationHistory) => {
        return exam.candidateStatusReason;
    }

    const downloadExamResultsAvailable = (exam: ExaminationHistory) => {
        if (exam.examineeStatusReasonId === ExamineeStatusReason.Invalidated || !exam.hasExamineeDocuments) return false;

        var examStartDate = getDateWithOffset(exam.startDate);

        if (exam.isPrimary && !exam.isMoc && examStartDate.getFullYear() >= validInitialSpecialtyCertYear) return true;

        return examStartDate.getFullYear() >= validForAnyExamAdministeredYear;
    }

    const showConfirmPersonalInfoMessage = (exam: ExaminationHistory) => {
        var result = new Date();
        result.setDate(result.getDate() - 120);

        var cutoff = getDateWithOffset(result.toString());
        var endDateCheck = getDateWithOffset(exam.endDate);

        var isInConfirmDateRange = endDateCheck >= cutoff;
        var isMoc = exam.isMoc;
        return exam.examinationGrade === ExaminationGrade.Pass && isInConfirmDateRange && !isMoc;
    }

    const showConfirmPersonalInfoMessageMOC = (exam: ExaminationHistory) => {
        var result = new Date();
        result.setDate(result.getDate() - 120);

        var cutoff = getDateWithOffset(result.toString());
        var endDateCheck = getDateWithOffset(exam.endDate);

        var isInConfirmDateRange = endDateCheck >= cutoff;
        var isMoc = exam.isMoc;
        return exam.examinationGrade === ExaminationGrade.Pass && isInConfirmDateRange && isMoc;
    }

    const downloadExamResult = async (e: React.MouseEvent<HTMLElement, MouseEvent>, examinationId: number) => {
        e.preventDefault();
        e.stopPropagation();

        setExaminationIdDownloading(examinationId);
        setIsDownloadingExamResult(true);

        var response = await abpnClient.getResponse(`/api/examination/DownloadScoreReport?examinationId=${examinationId}`, authContext)
        var blob = await response.blob();

        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            `ExaminationGrade.pdf`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);

        setIsDownloadingExamResult(false);
    }

    const downloadAbccAssessmentResult = async (e: React.MouseEvent<HTMLElement, MouseEvent>, abccAssessmentId: number) => {
        e.preventDefault();
        e.stopPropagation();

        setAbccAssessmentIdDownloading(abccAssessmentId);
        setIsDownloadingAbccAssessmentResult(true);

        var response = await abpnClient.getResponse(`/api/Physician/DownloadAbccAssessmentResult?abccAssessmentId=${abccAssessmentId}`, authContext)
        var blob = await response.blob();

        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            `ABCC Result.pdf`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);

        setIsDownloadingAbccAssessmentResult(false);
    }

    const getAbccAssessmentsForHistory = () => {
        return abccAssessmentHistory && abccAssessmentHistory.filter(assessment => assessment.modules.filter(module => module.grade !== ExaminationGrade.NoGrade).length > 0);
    };

    const displayAbccAssessmentHistory = () => {
        let abccAssessments = getAbccAssessmentsForHistory();
        let abccAssessmentsToDisplay = abccAssessments && abccAssessments
            .filter((abccAssessment: AbccAssessment) =>
                abccAssessment.grade !== ExaminationGrade.NoGrade &&
                abccAssessment.abccAssessmentStatusId === AbccAssessmentStatus.GradingComplete
            )
            .sort((a, b) => {
                // Convert endDate strings to Date objects using getDateWithOffset
                return getDateWithOffset(a.endDate).getTime() - getDateWithOffset(b.endDate).getTime();
            });
    
        return (
            <Fragment>
                <div>
                    <h5 className="font-weight-bolder mb-3">
                        Article Based Continuing Certification (ABCC) History
                    </h5>
                </div>
                <Card className="mb-4" border="pp-secondary-grey">
                    <Card.Body>
                        {(abccAssessmentsToDisplay == null || abccAssessmentsToDisplay.length === 0) && 
                            <div>
                                <p><i>No Article Based Continuing Certification (ABCC) History</i></p>
                            </div>
                        }
                        {
                            abccAssessmentsToDisplay && abccAssessmentsToDisplay.length > 0 && abccAssessmentsToDisplay.map(abccAssessment => {
                                let abccAssessmentStartDate = getDateWithOffset(abccAssessment.startDate);
                                let abccAssessmentEndDate = getDateWithOffset(abccAssessment.endDate);
    
                                const getGradeDescription = () => {
                                    switch (abccAssessment.grade) {
                                        case ExaminationGrade.Pass:
                                            return "Pass";
                                        case ExaminationGrade.Fail:
                                            return "Fail";
                                        default:
                                            return "No Grade";
                                    }
                                };
    
                                return (
                                    <Fragment>
                                        <Row className={`ccassessment`} key={abccAssessment.abccAssessmentId}>
                                            <Col>
                                                <div className="font-weight-bold">
                                                    {abccAssessment.certificationName.toUpperCase()} {abccAssessmentStartDate.getFullYear()} - {abccAssessmentEndDate.getFullYear()}
                                                </div>
                                            </Col>
                                            <Col>
                                                <div className="font-weight-bold">
                                                    Result: {getGradeDescription()}&nbsp;
                                                    {(isDownloadingAbccAssessmentResult && abccAssessmentIdDownloading === abccAssessment.abccAssessmentId) ? 
                                                        <AbpnSpinner /> :
                                                        <Button variant="outline-pp-primary" onClick={(e) => downloadAbccAssessmentResult(e, abccAssessment.abccAssessmentId)}>
                                                            Download Result
                                                        </Button>
                                                    }
                                                </div>
                                            </Col>
                                        </Row>
                                        <hr />
                                    </Fragment>
                                    
                                    
                                );
                            })
                        }
                    </Card.Body>
                </Card>
            </Fragment>
        );
    };
    
    

    const displayExamHistory = () => {
        if (examHistory == null || examHistory.length === 0) return <div><h4>No Examination History</h4></div>

        return (<Fragment>
            <div >
                <h5 className="font-weight-bolder mb-3">Examination History</h5>
            </div>
            <Card className="mb-4" border="pp-secondary-grey">
                <Card.Body>
                {examHistory.map(exam => {
                    return (
                        <Fragment>
                            <Row noGutters className="mt-3" key={exam.examinationId}>
                                <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">{exam.certificationName.toUpperCase()}</div>
                                    <div className="mb-1 pp-font-size-smaller">{exam.moduleSelection}{exam.formattedCnpType}</div>
                                </Col>
                                <Col xs={12} lg={10} xl={10}>
                                    <Row>
                                        <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Exam Start Date</div>
                                            <div className="mb-1 pp-font-size-smaller">{getDateWithOffset(exam.startDate).toLocaleDateString()}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Examination Name</div>
                                            <div className="mb-1 pp-font-size-smaller">{exam.title}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Grade</div>
                                            <div className="mb-1 pp-font-size-smaller">{getGrade(exam)}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Application Status</div>
                                            <div className="mb-1 pp-font-size-smaller">{getCandidateStatusDescription(exam)}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                            {downloadExamResultsAvailable(exam) ?
                                            (isDownloadingExamResult && examinationIdDownloading === exam.examinationId) ? <AbpnSpinner /> :
                                            <Button variant="outline-pp-primary" onClick={(e) => downloadExamResult(e, exam.examinationId)}>Download Exam Results</Button> : null
                                            }
                                        </Col>
                                    </Row>
                                    {showConfirmPersonalInfoMessage(exam) &&
                                    <Fragment>
                                        <hr />
                                        <Row>
                                            <Col className="mb-1" xs={12} md={12} lg={8} xl={8}>
                                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                    Confirm your Personal Information and Addresses within 30 days of the date of your grade letter.
                                                </div>
                                                <div className="mb-1 pp-font-size-smaller">
                                                    If you are receiving a <i>new</i> certificate, please confirm your Addresses, Name, and other Personal Information within 30 days 
                                                    of the date on your Grade Letter to ensure that your wall certificate is printed with the correct name and delivered to the 
                                                    correct shipping address.  The ABPN can take no responsibility for any certificates that cannot be delivered because of an 
                                                    incorrect or incomplete shipping address supplied to us through your ABPN Physician Portal.
                                                </div>
                                            </Col>
                                            <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                                <Link to="/AccountInformation">
                                                    <Button variant="pp-primary">Confirm Personal Information</Button>
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Fragment>
                                    }
                                    {showConfirmPersonalInfoMessageMOC(exam) &&
                                    <Fragment>
                                        <hr />
                                        <Row>
                                            <Col className="mb-1" xs={12} md={12} lg={8} xl={8}>
                                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                    Confirm your Personal Information and Addresses within 30 days of the date of your grade letter.
                                                </div>
                                                <div className="mb-1 pp-font-size-smaller">
                                                    If you are receiving a <i>new</i> certificate, please confirm your Addresses, Name, and other Personal Information within 30 days 
                                                    of the date on your Grade Letter to ensure that your wall certificate is printed with the correct name and delivered to the 
                                                    correct shipping address.  The ABPN can take no responsibility for any certificates that cannot be delivered because of an 
                                                    incorrect or incomplete shipping address supplied to us through your ABPN Physician Portal.
                                                </div>
                                            </Col>
                                            <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                                <Link to="/AccountInformation">
                                                    <Button variant="pp-primary">Confirm Personal Information</Button>
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Fragment>
                                    }
                                </Col>
                            </Row>
                            <hr />
                        </Fragment>
                    )
                })}
                </Card.Body>
            </Card>
        </Fragment>)
    }

    if (isLoading) return <AbpnSpinner></AbpnSpinner>;

    
    return (
        <Container>
            { displayAbccAssessmentHistory() }
            { displayExamHistory() }
        </Container>
    );
}